import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, Phone, Clock, Star, ChevronRight, Gift, Eye, EyeOff,
  Share2, Heart, Navigation, Check, ExternalLink, X, User
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { GOOGLE_MAPS_API_KEY, flag } from '../config';
import { api, errorMessage, getToken } from '../lib/api';
import { toast } from '../components/ui/sonner';
import SEO from '../components/SEO';
import { localBusinessJsonLd } from '../lib/structuredData';

/** Short human date for a deal end date; empty string if it will not parse. */
const formatDealDate = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/**
 * Whole months since a business joined Tappd. Returns null when we have no
 * join date and 'New' under a month — never a made-up number (PD-11).
 */
const monthsOnTappd = (createdAt) => {
  if (!createdAt) return null;
  const joined = new Date(createdAt);
  if (Number.isNaN(joined.getTime())) return null;
  const now = new Date();
  const months =
    (now.getFullYear() - joined.getFullYear()) * 12 + (now.getMonth() - joined.getMonth()) -
    (now.getDate() < joined.getDate() ? 1 : 0);
  return months < 1 ? 'New' : String(months);
};

// ============================================
// GOOGLE LOGO SVG COMPONENT
// ============================================
const GoogleLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

// ============================================
// MAIN COMPONENT
// ============================================
const BusinessLanding = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, claimDeal, openAuthModal } = useAuth();
  
  // ----- STATE -----
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Deal management
  const [selectedDealIndex, setSelectedDealIndex] = useState(0);
  const [dealStates, setDealStates] = useState({});      // { [dealId]: 'unclaimed' | 'claimed' | 'revealed' }
  const [claimCodes, setClaimCodes] = useState({});      // { [dealId]: 'ABC123' }
  const [claimLoading, setClaimLoading] = useState({});  // { [dealId]: boolean }
  
  // Nearby businesses
  const [nearbyBusinesses, setNearbyBusinesses] = useState([]);
  
  // Mobile sticky CTA visibility
  const [showMobileCta, setShowMobileCta] = useState(false);
  const dealPickerRef = useRef(null);
  
  // ----- COMPUTED VALUES -----
  // Phase 1 (ADR-0009, PD-12): there is no hub unlock gate. Deals at a venue are
  // claimable by any signed-in patron, so no `isHubUnlocked` branch exists here.
  const selectedDeal = business?.deals?.[selectedDealIndex];
  const totalClaimedThisWeek = business?.deals?.reduce((sum, d) => sum + (d.total_claims || 0), 0) || 0;
  
  // ----- EFFECTS -----
  
  // Fetch business on mount
  useEffect(() => {
    fetchBusiness();
  }, [slug]);
  
  // Check existing claims when authenticated
  useEffect(() => {
    if (isAuthenticated && business?.deals?.length) {
      checkAllDealClaims();
    }
  }, [isAuthenticated, business?.id]);
  
  // Fetch nearby businesses
  useEffect(() => {
    if (business) {
      fetchNearbyBusinesses();
    }
  }, [business?.id]);
  
  // Mobile sticky CTA scroll handler
  useEffect(() => {
    const handleScroll = () => {
      if (dealPickerRef.current) {
        const rect = dealPickerRef.current.getBoundingClientRect();
        setShowMobileCta(rect.bottom < 0);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // ----- API FUNCTIONS -----
  
  const fetchBusiness = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data } = await api.get(`/businesses/${slug}`);
      setBusiness(data);
      
      // Initialize all deals as unclaimed
      const initialStates = {};
      data.deals?.forEach(deal => {
        initialStates[deal.id] = 'unclaimed';
      });
      setDealStates(initialStates);
      
    } catch (err) {
      setError(errorMessage(err, 'Business not found'));
    } finally {
      setLoading(false);
    }
  };
  
  const checkAllDealClaims = async () => {
    if (!getToken() || !business?.deals) return;

    // One request per deal, but issued together — this used to be a serial
    // `for` loop, so a five-deal business paid five round trips in sequence
    // on every page load (audit row 103).
    const results = await Promise.all(
      business.deals.map(async (deal) => {
        try {
          const { data } = await api.get(`/deals/check/${deal.id}`);
          return { dealId: deal.id, claimed: !!data.claimed, code: data.code };
        } catch {
          return { dealId: deal.id, claimed: false };
        }
      }),
    );

    const newStates = { ...dealStates };
    const newCodes = { ...claimCodes };
    results.forEach(({ dealId, claimed, code }) => {
      if (claimed) {
        newStates[dealId] = 'claimed'; // Start as claimed (code hidden)
        newCodes[dealId] = code;
      }
    });

    setDealStates(newStates);
    setClaimCodes(newCodes);
  };
  
  const handleClaimDeal = async (deal) => {
    // Check authentication
    if (!isAuthenticated) {
      openAuthModal('login');
      return;
    }
    
    setClaimLoading(prev => ({ ...prev, [deal.id]: true }));
    
    try {
      const result = await claimDeal({
        business_id: business.id,
        deal_id: deal.id,
        business_name: business.name,
        business_slug: business.slug,
        deal_title: deal.title,
        deal_description: deal.description
      });
      
      if (result.success) {
        // Update state
        setDealStates(prev => ({ ...prev, [deal.id]: 'claimed' }));
        setClaimCodes(prev => ({ ...prev, [deal.id]: result.data.code }));
        
        // Trigger confetti
        createConfetti();
      } else {
        toast.error(result.error || 'We could not claim that deal. Please try again.');
      }
    } catch (err) {
      toast.error(errorMessage(err, 'We could not claim that deal. Please try again.'));
    } finally {
      setClaimLoading(prev => ({ ...prev, [deal.id]: false }));
    }
  };
  
  const handleRevealCode = (dealId) => {
    setDealStates(prev => ({ ...prev, [dealId]: 'revealed' }));
  };
  
  const fetchNearbyBusinesses = async () => {
    try {
      const { data } = await api.get('/businesses', { params: { limit: 6 } });
      // Filter out current business
      const filtered = (data.businesses || data)
        .filter(b => b.id !== business.id)
        .slice(0, 3);
      setNearbyBusinesses(filtered);
    } catch (err) {
      console.error('Error fetching nearby businesses:', err);
    }
  };
  
  // ----- CONFETTI ANIMATION -----
  const createConfetti = () => {
    const container = document.getElementById('confetti-container');
    if (!container) return;
    
    container.classList.remove('hidden');
    const colors = ['#f97316', '#fbbf24', '#34d399', '#60a5fa', '#a78bfa', '#f472b6'];
    
    for (let i = 0; i < 100; i++) {
      const confetti = document.createElement('div');
      confetti.className = 'confetti-piece';
      confetti.style.left = Math.random() * 100 + 'vw';
      confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      confetti.style.animationDelay = Math.random() * 2 + 's';
      confetti.style.animationDuration = (Math.random() * 2 + 2) + 's';
      container.appendChild(confetti);
    }
    
    setTimeout(() => {
      container.innerHTML = '';
      container.classList.add('hidden');
    }, 5000);
  };
  
  // ----- HELPER FUNCTIONS -----
  
  const formatAddress = (business) => {
    if (!business) return '';

    // If business.address is already a formatted string, try to enhance it
    // Otherwise build from component fields
    const parts = [];

    // Use separate fields if available (preferred)
    if (business.street_address || business.city || business.state) {
      if (business.street_address) parts.push(business.street_address);
      if (business.city) parts.push(business.city);
      if (business.state && business.zip_code) {
        parts.push(`${business.state} ${business.zip_code}`);
      } else if (business.state) {
        parts.push(business.state);
      } else if (business.zip_code) {
        parts.push(business.zip_code);
      }
      return parts.join(', ');
    }

    // Fallback to address field if it's an object
    if (business.address && typeof business.address === 'object') {
      if (business.address.street) parts.push(business.address.street);
      if (business.address.city) parts.push(business.address.city);
      if (business.address.state && business.address.zip_code) {
        parts.push(`${business.address.state} ${business.address.zip_code}`);
      } else if (business.address.state) {
        parts.push(business.address.state);
      }
      return parts.join(', ');
    }

    // Fallback to address string
    if (business.address && typeof business.address === 'string') {
      return business.address;
    }

    return '';
  };
  
  const formatHours = (hours) => {
    if (!hours || !Array.isArray(hours)) return null;
    
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const today = new Date().getDay();
    
    return hours.map((h) => ({
      day: days[h.day_of_week],
      hours: h.is_closed ? 'Closed' : 
             h.is_24_hours ? '24 Hours' :
             `${formatTime(h.open_time)} - ${formatTime(h.close_time)}`,
      isToday: h.day_of_week === today,
      isClosed: h.is_closed
    }));
  };
  
  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };
  
  const getCurrentStatus = () => {
    if (!business?.hours_of_operation) return null;
    
    const now = new Date();
    const today = now.getDay();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    const todayHours = business.hours_of_operation.find(h => h.day_of_week === today);
    if (!todayHours || todayHours.is_closed) return { open: false, text: 'Closed' };
    if (todayHours.is_24_hours) return { open: true, text: 'Open 24 Hours' };
    
    const [openH, openM] = (todayHours.open_time || '00:00').split(':').map(Number);
    const [closeH, closeM] = (todayHours.close_time || '00:00').split(':').map(Number);
    const openMinutes = openH * 60 + openM;
    const closeMinutes = closeH * 60 + closeM;
    
    if (currentTime >= openMinutes && currentTime < closeMinutes) {
      return { open: true, text: `Open · Closes ${formatTime(todayHours.close_time)}` };
    }
    return { open: false, text: `Closed · Opens ${formatTime(todayHours.open_time)}` };
  };
  
  const getCategoryEmoji = (category) => {
    const emojis = {
      'Food & Dining': '🍽️',
      'Coffee & Breakfast': '☕',
      'Bars & Nightlife': '🍺',
      'Personal Services': '🌸',
      'Health & Wellness': '💪',
      'Retail': '🛍️',
      'Entertainment': '🎬',
      'Professional Services': '💼',
      'Pet Services': '🐾',
      'Home & Garden': '🏡',
      'Beauty & Wellness': '💇',
      'Sports & Recreation': '🚴',
      'Restaurant': '🍕'
    };
    return emojis[category] || '🏪';
  };
  
  const getDealEmoji = (index) => {
    const emojis = ['🎁', '🎀', '🌸', '☕', '🍕', '💇', '🎬', '🛍️'];
    return emojis[index % emojis.length];
  };
  
  // ----- RENDER -----
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FEF7ED] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
      </div>
    );
  }
  
  if (error || !business) {
    return (
      <div className="min-h-screen bg-[#FEF7ED] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Business Not Found</h2>
          <p className="text-gray-600 mb-4">The business you&apos;re looking for doesn&apos;t exist.</p>
          <Link to="/local-deals-near-me" className="text-orange-500 hover:underline">
            ← Back to Deals
          </Link>
        </div>
      </div>
    );
  }
  
  const status = getCurrentStatus();
  const formattedHours = formatHours(business.hours_of_operation);
  const address = formatAddress(business);
  
  return (
    <div className="min-h-screen bg-[#FEF7ED]">
      <SEO 
        title={business?.name || 'Business'}
        description={business?.deals?.[0]?.description || `Check out ${business?.name}'s special offers and deals on Tappd. Claim local discounts at this business.`}
        url={`/business/${slug}`}
        image={business?.image_url || business?.logo_url}
        jsonLd={localBusinessJsonLd(business, slug)}
      />
      {/* Confetti Container */}
      <div id="confetti-container" className="fixed inset-0 pointer-events-none z-50 hidden overflow-hidden"></div>
      
      {/* ============================================ */}
      {/* HERO SECTION */}
      {/* ============================================ */}
      <section className="relative min-h-[70vh] md:min-h-[60vh] overflow-hidden" style={{ paddingTop: '8rem' }}>
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={business.image_url || business.logo_url || 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1920'}
            alt={business.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1e1b4b] via-[#1e1b4b]/60 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#1e1b4b]/40 to-transparent"></div>
        </div>
        
        {/* Breadcrumb */}
        <div className="relative z-10 px-6 mt-4">
          <div className="max-w-6xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-white/60">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/local-deals-near-me" className="hover:text-white transition-colors">Deals</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white">{business.name}</span>
            </nav>
          </div>
        </div>
        
        {/* Hero Content */}
        <div className="relative z-10 px-6 pt-12 pb-24 md:pt-20 md:pb-32">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-2xl">
              {/* Category Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-500/20 backdrop-blur-sm rounded-full text-orange-300 text-sm font-medium mb-4">
                <span>{getCategoryEmoji(business.category)}</span>
                <span>{business.category || 'Local Business'}</span>
              </div>
              
              {/* Business Name */}
              <h1 className="font-display text-4xl md:text-6xl text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                {business.name}
              </h1>
              
              {/* Stats Row */}
              <div className="flex flex-wrap items-center gap-4 mb-6">
                {/* Google Rating */}
                {business.google_rating && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full">
                    <GoogleLogo className="w-5 h-5" />
                    <span className="text-white font-semibold">{business.google_rating}</span>
                    <span className="text-white/60 text-sm">
                      ({business.google_total_reviews || 0} reviews)
                    </span>
                  </div>
                )}
                
                {/* Claimed count */}
                {totalClaimedThisWeek > 0 && (
                  <div className="flex items-center gap-2 text-white/70 text-sm">
                    <Check className="w-4 h-4 text-green-400" />
                    <span>
                      <strong className="text-green-400">{totalClaimedThisWeek} people</strong> claimed so far
                    </span>
                  </div>
                )}
              </div>
              
              {/* Quick Actions */}
              <div className="flex flex-wrap gap-3">
                {business.phone && (
                  <a 
                    href={`tel:${business.phone}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    Call Now
                  </a>
                )}
                
                {address && (
                  <a 
                    href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium transition-colors"
                  >
                    <Navigation className="w-4 h-4" />
                    Directions
                  </a>
                )}
                
                {status && (
                  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                    status.open 
                      ? 'bg-green-500/20 text-green-300' 
                      : 'bg-red-500/20 text-red-300'
                  }`}>
                    <Clock className="w-4 h-4" />
                    {status.text}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* ============================================ */}
      {/* MAIN CONTENT */}
      {/* ============================================ */}
      <main className="relative z-10 -mt-8 md:-mt-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-8">
            
            {/* LEFT COLUMN */}
            <div className="lg:col-span-2 space-y-8">
              
              {/* DEAL PICKER */}
              {business.deals?.length > 1 && (
                <div ref={dealPickerRef} className="bg-white/80 backdrop-blur rounded-2xl p-4 shadow-lg">
                  <p className="text-sm text-[#1e1b4b]/60 mb-3">
                    This business has <strong className="text-[#1e1b4b]">{business.deals.length} active deals</strong>:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {business.deals.map((deal, index) => {
                      const isSelected = index === selectedDealIndex;
                      const isClaimed = dealStates[deal.id] !== 'unclaimed';
                      
                      return (
                        <button
                          key={deal.id}
                          onClick={() => setSelectedDealIndex(index)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                            isSelected
                              ? 'bg-orange-500 text-white shadow-md'
                              : 'bg-white text-[#1e1b4b] border-2 border-[#1e1b4b]/10 hover:border-orange-300'
                          }`}
                        >
                          <span>{isClaimed ? '✓' : getDealEmoji(index)}</span>
                          <span className="truncate max-w-[150px]">{deal.title}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Single deal ref for mobile CTA */}
              {business.deals?.length === 1 && <div ref={dealPickerRef}></div>}
              
              {/* DEAL CARD */}
              {selectedDeal && (
                <DealCard
                  deal={selectedDeal}
                  business={business}
                  state={dealStates[selectedDeal.id] || 'unclaimed'}
                  code={claimCodes[selectedDeal.id]}
                  isLoading={claimLoading[selectedDeal.id]}
                  isAuthenticated={isAuthenticated}
                  onClaim={() => handleClaimDeal(selectedDeal)}
                  onReveal={() => handleRevealCode(selectedDeal.id)}
                  onLogin={() => openAuthModal('login')}
                  address={address}
                />
              )}
              
              {/* ABOUT SECTION */}
              {business.description && (
                <div className="bg-white rounded-3xl shadow-lg p-6 md:p-8">
                  <h3 className="font-display text-xl text-[#1e1b4b] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                    About This Place
                  </h3>
                  <p className="text-[#1e1b4b]/70 leading-relaxed">
                    {business.description}
                  </p>
                </div>
              )}
              
              {/* REVIEWS SECTION */}
              {business.google_rating && (
                <div className="bg-white rounded-3xl shadow-lg p-6 md:p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-display text-xl text-[#1e1b4b]" style={{ fontFamily: "'Playfair Display', serif" }}>
                      Reviews
                    </h3>
                    <a 
                      href={`https://www.google.com/search?q=${encodeURIComponent(business.name + ' ' + (business.address?.city || ''))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-500 hover:text-orange-600 text-sm font-medium"
                    >
                      View all {business.google_total_reviews || 0}
                    </a>
                  </div>
                  
                  {/* Rating Overview */}
                  <div className="flex items-center gap-6 p-4 bg-gray-50 rounded-2xl mb-6">
                    <div className="text-center">
                      <div className="text-5xl font-bold text-[#1e1b4b]">{business.google_rating}</div>
                      <div className="flex gap-0.5 mt-1 justify-center">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-5 h-5 ${star <= Math.round(business.google_rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                      <p className="text-sm text-[#1e1b4b]/50 mt-1">{business.google_total_reviews || 0} reviews</p>
                    </div>
                    
                  </div>
                </div>
              )}
              
            </div>
            
            {/* RIGHT SIDEBAR */}
            <div className="space-y-6">
              {/* Business Info Card */}
              <div className="bg-white rounded-3xl shadow-lg p-6 sticky top-28 z-20">
                <h3 className="font-display text-xl text-[#1e1b4b] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Business Info
                </h3>
                
                <div className="space-y-4">
                  {/* Address */}
                  {address && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium text-[#1e1b4b]">Address</p>
                        <p className="text-[#1e1b4b]/60 text-sm">{address}</p>
                        <a 
                          href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-orange-500 hover:underline flex items-center gap-1 mt-1"
                        >
                          Get Directions <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {/* Phone */}
                  {business.phone && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium text-[#1e1b4b]">Phone</p>
                        <a href={`tel:${business.phone}`} className="text-orange-500 hover:text-orange-600 font-medium">
                          {business.phone}
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {/* Hours */}
                  {formattedHours && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-[#1e1b4b] mb-2">Hours</p>
                        <div className="text-sm space-y-1">
                          {formattedHours.map((h, i) => (
                            <div 
                              key={i}
                              className={`flex justify-between py-1 ${h.isToday ? 'font-semibold text-[#1e1b4b]' : 'text-[#1e1b4b]/70'}`}
                            >
                              <span>{h.day}</span>
                              <span className={h.isClosed ? 'text-red-500' : ''}>{h.hours}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Map */}
                  {address && (
                    <div className="mt-4">
                      {GOOGLE_MAPS_API_KEY ? (
                        <>
                          <div className="h-48 bg-gray-100 rounded-xl overflow-hidden">
                            <iframe
                              src={`https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(address)}`}
                              width="100%"
                              height="100%"
                              style={{ border: 0 }}
                              allowFullScreen
                              loading="lazy"
                              referrerPolicy="no-referrer-when-downgrade"
                              title={`Map of ${business?.name || 'business location'}`}
                            ></iframe>
                          </div>
                          <a
                            href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-full mt-3 py-3 bg-[#1e1b4b] hover:bg-[#2d2a5e] rounded-xl text-white font-medium text-center transition-colors"
                          >
                            Open in Maps
                          </a>
                        </>
                      ) : (
                        <a
                          href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block p-6 bg-gradient-to-br from-[#1e1b4b] to-[#2d2a5e] rounded-xl text-white hover:shadow-lg transition-all"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-white/10 rounded-lg">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                              </div>
                              <div>
                                <div className="font-semibold">View on Google Maps</div>
                                <div className="text-sm text-white/70">Get directions</div>
                              </div>
                            </div>
                            <svg className="w-5 h-5 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Tappd Verified Badge */}
              <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-3xl p-6 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <img src="/tappd-logo.png" alt="Tappd" className="h-6 brightness-0 invert" />
                  <span className="text-white/80 text-sm">Verified Partner</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/20 backdrop-blur rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold">{totalClaimedThisWeek}</div>
                    <div className="text-xs text-white/80">Deals Claimed</div>
                  </div>
                  {monthsOnTappd(business.created_at) && (
                    <div className="bg-white/20 backdrop-blur rounded-xl p-3 text-center">
                      <div className="text-2xl font-bold">{monthsOnTappd(business.created_at)}</div>
                      <div className="text-xs text-white/80">
                        {monthsOnTappd(business.created_at) === 'New' ? 'On Tappd' : 'Months on Tappd'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </main>
      
      {/* MORE DEALS NEARBY */}
      {nearbyBusinesses.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 py-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-display text-2xl md:text-3xl text-[#1e1b4b]" style={{ fontFamily: "'Playfair Display', serif" }}>
              More Deals Nearby
            </h2>
            <Link to="/local-deals-near-me" className="text-orange-500 hover:text-orange-600 font-medium flex items-center gap-1">
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {nearbyBusinesses.map(b => (
              <Link 
                key={b.id}
                to={`/business/${b.slug}`}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
              >
                <div className="h-36 bg-gradient-to-br from-orange-400 to-amber-500 relative">
                  <img 
                    src={b.image_url || b.logo_url || 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400'}
                    alt={b.name}
                    className="w-full h-full object-cover"
                  />
                  {b.deals?.[0]?.title && (
                    <div className="absolute top-3 right-3 px-2 py-1 bg-white/90 backdrop-blur rounded-full text-xs font-semibold text-orange-600">
                      {b.deals[0].title.length > 15 ? b.deals[0].title.substring(0, 15) + '...' : b.deals[0].title}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-[#1e1b4b] mb-1">{b.name}</h3>
                  <p className="text-sm text-orange-600 font-medium mb-2">
                    {b.offer || b.deals?.[0]?.description?.substring(0, 50) || 'Special Offer'}
                  </p>
                  <div className="flex items-center justify-between text-sm text-[#1e1b4b]/50">
                    {b.google_rating && (
                      <span className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                        <span>{b.google_rating}</span>
                      </span>
                    )}
                    <span>0.5 mi</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
      
      {/* Mobile Sticky CTA */}
      <AnimatePresence>
        {showMobileCta && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-0 left-0 right-0 z-50 p-4 md:hidden"
            style={{ background: 'linear-gradient(to top, white 80%, transparent)' }}
          >
            <button
              onClick={() => dealPickerRef.current?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full py-4 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl text-white font-bold text-lg shadow-xl flex items-center justify-center gap-2"
            >
              <span>View Available Deals</span>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* CSS for confetti and fonts */}
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        .confetti-piece {
          position: absolute;
          width: 10px;
          height: 10px;
          animation: confetti-fall 3s linear forwards;
        }
        .font-display {
          font-family: 'Playfair Display', serif;
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .shimmer {
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%);
          background-size: 200% 100%;
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
};

// ============================================
// DEAL CARD COMPONENT
// ============================================
const DealCard = ({ 
  deal, 
  business, 
  state, 
  code, 
  isLoading, 
  isAuthenticated, 
  onClaim, 
  onReveal,
  onLogin,
  address
}) => {
  
  // Unclaimed State
  if (state === 'unclaimed') {
    return (
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden transition-all hover:-translate-y-1 hover:shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                <Gift className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white/80 text-sm font-medium">{business.name.toUpperCase()}</p>
                <p className="text-white font-bold">
                  {deal.end_date ? `Ends ${formatDealDate(deal.end_date)}` : 'Local Deal'}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 md:p-8">
          <h2 className="font-display text-3xl md:text-4xl text-[#1e1b4b] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            {deal.title}
          </h2>
          <p className="text-[#1e1b4b]/60 mb-4">
            {deal.description}
          </p>
          
          {/* Terms — every chip is backed by a real field or an enforced rule.
              "No minimum purchase" used to sit here with nothing behind it. */}
          <div className="flex flex-wrap gap-3 mb-6">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
              <Clock className="w-4 h-4" aria-hidden="true" />
              <span>Code valid 7 days after you claim</span>
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
              <User className="w-4 h-4" aria-hidden="true" />
              <span>One claim per person</span>
            </span>
            {deal.end_date && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm">
                <Check className="w-4 h-4" aria-hidden="true" />
                <span>Offer ends {formatDealDate(deal.end_date)}</span>
              </span>
            )}
            {deal.generate_lead && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                <Check className="w-4 h-4" aria-hidden="true" />
                <span>{business.name} will follow up with you</span>
              </span>
            )}
          </div>
          
          {/* Claim Button */}
          {!isAuthenticated ? (
            <div className="space-y-3">
              <p className="text-center text-[#1e1b4b]/60 mb-4">Sign in to claim this offer</p>
              <button
                onClick={onLogin}
                className="w-full py-4 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl text-white font-bold text-lg shadow-lg shadow-orange-500/30 hover:from-orange-600 hover:to-orange-700 transition-all flex items-center justify-center gap-3"
              >
                <span>Sign In to Claim</span>
                {flag('REWARDS') && <span className="px-2 py-0.5 bg-white/20 rounded-full text-sm font-semibold">+25 Taps</span>}
              </button>
            </div>
          ) : (
            <button
              onClick={onClaim}
              disabled={isLoading}
              className="relative w-full py-5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 rounded-2xl text-white font-bold text-xl transition-all shadow-lg shadow-orange-500/30 disabled:opacity-50 overflow-hidden group"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Claiming...
                </span>
              ) : (
                <>
                  <span className="relative z-10 flex items-center justify-center gap-3">
                    <Gift className="w-6 h-6 group-hover:scale-110 transition-transform" />
                    <span>Claim This Deal</span>
                    {flag('REWARDS') && <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-sm font-semibold">+25 Taps</span>}
                  </span>
                  <div className="absolute inset-0 shimmer"></div>
                </>
              )}
            </button>
          )}
          
          <p className="text-center text-sm text-[#1e1b4b]/50 mt-3">
            Free to claim · Show your code at the business to redeem
          </p>
        </div>
      </div>
    );
  }
  
  // Claimed State (code hidden or revealed)
  return (
    <div className="bg-white rounded-3xl shadow-xl overflow-hidden border-2 border-orange-200">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-100 to-orange-100 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-orange-200 rounded-xl flex items-center justify-center">
            <Gift className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <p className="text-orange-600 text-sm font-semibold tracking-wide">{business.name.toUpperCase()}</p>
            <h3 className="font-display text-xl text-[#1e1b4b]" style={{ fontFamily: "'Playfair Display', serif" }}>
              {deal.title}
            </h3>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-6 md:p-8 text-center">
        {/* Success Icon */}
        <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
          <Check className="w-8 h-8 text-green-500" />
        </div>
        
        <h3 className="font-display text-2xl text-green-600 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
          Offer Claimed!
        </h3>
        <p className="text-[#1e1b4b]/60 mb-6">Show this code to redeem your offer</p>
        
        {/* Code Box */}
        <div className="inline-block w-full max-w-xs mb-4">
          <div className="border-2 border-dashed border-orange-300 rounded-2xl p-4 bg-orange-50/50">
            <p className="text-sm text-[#1e1b4b]/60 mb-2 flex items-center justify-center gap-2">
              <span>🎟️</span>
              <span className="font-semibold tracking-wide">YOUR PROMO CODE</span>
            </p>
            
            {state === 'claimed' ? (
              // Hidden code
              <div className="flex items-center justify-center gap-2 py-2">
                {[...Array(6)].map((_, i) => (
                  <span key={i} className="w-4 h-4 rounded-full bg-orange-400"></span>
                ))}
              </div>
            ) : (
              // Revealed code
              <p className="text-3xl font-bold font-mono text-[#1e1b4b] tracking-widest py-1">
                {code}
              </p>
            )}
          </div>
        </div>
        
        {state === 'claimed' ? (
          // Show Code Button
          <button
            onClick={onReveal}
            className="w-full max-w-xs py-3 border-2 border-orange-400 rounded-full text-orange-500 font-semibold hover:bg-orange-50 transition-colors flex items-center justify-center gap-2 mx-auto"
          >
            <Eye className="w-5 h-5" />
            Show Code
          </button>
        ) : (
          // Actions after reveal
          <div className="space-y-4 max-w-xs mx-auto">
            <div className="flex items-center justify-center gap-2 text-sm text-[#1e1b4b]/60">
              <Clock className="w-4 h-4" />
              Valid for 7 days
            </div>
            <div className="flex gap-3">
              <a 
                href={`https://maps.google.com/?q=${encodeURIComponent(address || business.name)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-3 bg-[#1e1b4b] hover:bg-[#2d2a5e] rounded-xl text-white font-medium flex items-center justify-center gap-2 transition-colors"
              >
                <Navigation className="w-5 h-5" />
                Get Directions
              </a>
              <button className="flex-1 py-3 border-2 border-[#1e1b4b]/20 rounded-xl text-[#1e1b4b] font-medium flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors">
                <Share2 className="w-5 h-5" />
                Share
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessLanding;
